// assets/js/utils/site-from-form.js
/**
 * An event website filled in from an RSVP form — the ONE mapping used by the
 * site builder's "Fill my website", the /wedding-websites/preview page the
 * site-from-form email links to, and that email itself (names, design).
 * Dual-environment like event-category.js: window.SiteFromForm in the browser,
 * require() on the server. Pure: no DOM, no builder state.
 *
 * The tables below moved here from wedding-builder.js (2026-11-03) so the
 * preview and "Make it mine" can never fill a site two different ways.
 */
(function () {
    const EC = (typeof window !== 'undefined' && window.EventCategory)
        || (typeof require === 'function' ? require('./event-category') : null);

    const PARTY_TEMPLATE_IDS = ['fiesta', 'graffiti', 'retro-pop', 'quince-noche', 'quince-rosa'];
    // Templates with envelope art (/assets/images/wedding-envelope). The others
    // open on the plain cover (names and date), which is a tap all the same.
    const ENVELOPE_TEMPLATES = ['romantic-blush', 'classic-elegance', 'coastal-breeze',
        'sunset-romance', 'garden-party', 'modern-minimalist', 'khaleeji', 'quince-noche', 'quince-rosa'];
    const QUINCE_TEMPLATE_IDS = ['quince-noche', 'quince-rosa'];
    const isPartyTemplate = (id) => PARTY_TEMPLATE_IDS.includes(id);

    const LOCALIZED_WELCOME_MESSAGES = {
        'en': '<p>We are so excited to share our special day with you. Please join us as we celebrate our love and begin our new journey together.</p>',
        'he': '<p>אנחנו כל כך שמחים לחגוג את היום המיוחד הזה יחד איתכם. הצטרפו אלינו לחגיגה של האהבה שלנו ותחילת הדרך המשותפת.</p>',
        'es': '<p>Estamos muy emocionados de compartir nuestro día especial con ustedes. Únanse a nosotros para celebrar nuestro amor y comenzar este nuevo viaje juntos.</p>',
        'fr': '<p>Nous sommes très heureux de partager ce jour si spécial avec vous. Rejoignez-nous pour célébrer notre amour et le début de notre nouvelle vie ensemble.</p>',
        'de': '<p>Wir freuen uns sehr, unseren besonderen Tag mit euch zu teilen. Seid dabei, wenn wir unsere Liebe feiern und unsere gemeinsame Reise beginnen.</p>',
        'pt': '<p>Estamos muito felizes em compartilhar este dia especial com vocês. Junte-se a nós para celebrar nosso amor e o início de nossa jornada juntos.</p>',
        // Invitation wording, not "join us to celebrate our love": an Arabic
        // site starts as an invitation (applyArabicInvitationDefaults).
        'ar': '<p>بحضوركم يكتمل فرحنا، ونتشرّف بمشاركتكم أجمل أيام عمرنا.</p>'
    };

    // [with a name, without one] pairs take {name} = the first word of Name.
    // Section/blurb keys are the same ones site.html reads from labels.
    const PARTY_SITE_DEFAULTS = {
        en: {
            welcome: "<p>You're invited! Come celebrate with us. It won't be the same without you.</p>",
            ourStory: ['About {name}', 'About the guest of honor'],
            travel: 'Getting there',
            rsvpBlurb: ['Let {name} know if you can make it!', 'Let us know if you can make it!'],
            registryBlurb: "Your presence is the best present. If you'd still like to bring something, here are a few ideas:",
            fiesta: { details: 'The party', photos: 'Highlights' },
            graffiti: { details: 'The Night', photos: 'The Gallery' },
            'retro-pop': { details: 'The Plan', photos: 'Throwback Reel' },
            quince: { ourStory: ['About {name}', 'About the birthday girl'], details: 'The celebration', photos: ['{name}’s moments', 'Moments to remember'],
                rsvpBlurb: ['Let {name} know if you can celebrate with her.', 'Let us know if you can celebrate with us.'] }
        },
        he: {
            welcome: '<p>אתם מוזמנים! בואו לחגוג איתנו, בלעדיכם זה פשוט לא אותו דבר.</p>',
            ourStory: ['קצת על {name}', 'קצת על בעל/ת השמחה'],
            travel: 'איך מגיעים',
            rsvpBlurb: ['עדכנו את {name} אם אתם מגיעים!', 'עדכנו אותנו אם אתם מגיעים!'],
            registryBlurb: 'הנוכחות שלכם היא המתנה הכי טובה. ואם בכל זאת תרצו להביא משהו, הנה כמה רעיונות:',
            fiesta: { details: 'המסיבה', photos: 'רגעים נבחרים' },
            graffiti: { details: 'הערב', photos: 'הגלריה' },
            'retro-pop': { details: 'התוכנית', photos: 'פלאשבקים' },
            quince: { ourStory: ['קצת על {name}', 'קצת על בת ה-15'], details: 'החגיגה', photos: ['הרגעים של {name}', 'רגעים לזכור'],
                rsvpBlurb: ['עדכנו את {name} אם אתם באים לחגוג איתה!', 'עדכנו אותנו אם אתם באים לחגוג איתנו!'] }
        },
        ar: {
            welcome: '<p>يسعدنا أن تشاركونا الاحتفال، فالفرحة لا تكتمل إلا بوجودكم.</p>',
            ourStory: ['نبذة عن {name}', 'نبذة عن صاحب المناسبة'],
            travel: 'كيفية الوصول',
            rsvpBlurb: ['أخبروا {name} إن كنتم ستحضرون!', 'أخبرونا إن كنتم ستحضرون!'],
            registryBlurb: 'حضوركم أجمل هدية، وإن رغبتم في إحضار شيء فإليكم بعض الأفكار:',
            fiesta: { details: 'الحفلة', photos: 'أجمل اللقطات' },
            graffiti: { details: 'السهرة', photos: 'معرض الصور' },
            'retro-pop': { details: 'البرنامج', photos: 'ذكريات زمان' },
            quince: { ourStory: ['نبذة عن {name}', 'نبذة عن صاحبة المناسبة'], details: 'الاحتفال', photos: ['لحظات {name}', 'لحظات لا تُنسى'],
                rsvpBlurb: ['أخبروا {name} إن كنتم ستحتفلون معها!', 'أخبرونا إن كنتم ستحتفلون معنا!'] }
        },
        es: {
            welcome: '<p>¡Te esperamos! Ven a celebrar con nosotros: sin ti no sería lo mismo.</p>',
            ourStory: ['Sobre {name}', 'Sobre el festejado'],
            travel: 'Cómo llegar',
            rsvpBlurb: ['¡Confírmale a {name} si podrás venir!', '¡Confírmanos si podrás venir!'],
            registryBlurb: 'Tu presencia es el mejor regalo. Si aun así quieres traer algo, aquí tienes algunas ideas:',
            fiesta: { details: 'La fiesta', photos: 'Momentos destacados' },
            graffiti: { details: 'La noche', photos: 'La galería' },
            'retro-pop': { details: 'El plan', photos: 'Recuerdos retro' },
            quince: { ourStory: ['Sobre {name}', 'Sobre la quinceañera'], details: 'La celebración', photos: ['Momentos de {name}', 'Momentos para recordar'],
                rsvpBlurb: ['Confírmale a {name} si podrás celebrar con ella.', 'Confírmanos si podrás celebrar con nosotros.'] }
        },
        fr: {
            welcome: '<p>Vous êtes invités ! Venez faire la fête avec nous : sans vous, ce ne serait pas pareil.</p>',
            ourStory: ['À propos de {name}', 'Qui fête-t-on ?'],
            travel: 'Comment venir',
            rsvpBlurb: ['Dites à {name} si vous serez là !', 'Dites-nous si vous serez là !'],
            registryBlurb: 'Votre présence est le plus beau des cadeaux. Si vous souhaitez tout de même offrir quelque chose, voici quelques idées :',
            fiesta: { details: 'La fête', photos: 'Temps forts' },
            graffiti: { details: 'La soirée', photos: 'La galerie' },
            'retro-pop': { details: 'Le programme', photos: 'Rétro en images' },
            quince: { details: 'La célébration', photos: ['Les moments de {name}', 'Moments inoubliables'],
                rsvpBlurb: ['Dites à {name} si vous pourrez fêter ça avec elle.', 'Dites-nous si vous pourrez être des nôtres.'] }
        },
        de: {
            welcome: '<p>Ihr seid eingeladen! Feiert mit uns – ohne euch wäre es nur halb so schön.</p>',
            ourStory: ['Über {name}', 'Über die Hauptperson'],
            travel: 'Anreise',
            rsvpBlurb: ['Sagt {name} Bescheid, ob ihr kommt!', 'Sagt uns Bescheid, ob ihr kommt!'],
            registryBlurb: 'Eure Anwesenheit ist das schönste Geschenk. Wer trotzdem etwas mitbringen möchte, findet hier ein paar Ideen:',
            fiesta: { details: 'Die Party', photos: 'Highlights' },
            graffiti: { details: 'Die Nacht', photos: 'Die Galerie' },
            'retro-pop': { details: 'Der Plan', photos: 'Zeitreise in Bildern' },
            quince: { ourStory: ['Über {name}', 'Über das Geburtstagskind'], details: 'Die Feier', photos: ['Momente mit {name}', 'Unvergessliche Momente'],
                rsvpBlurb: ['Sagt {name} Bescheid, ob ihr mit ihr feiert.', 'Sagt uns Bescheid, ob ihr dabei seid.'] }
        },
        pt: {
            welcome: '<p>Vem comemorar com a gente! Sem você não seria a mesma coisa.</p>',
            ourStory: ['Sobre {name}', 'Sobre o homenageado'],
            travel: 'Como chegar',
            rsvpBlurb: ['Avise {name} se você vai poder vir!', 'Avise a gente se você vai poder vir!'],
            registryBlurb: 'Sua presença é o melhor presente. Mas, se quiser trazer algo, aqui vão algumas ideias:',
            fiesta: { details: 'A festa', photos: 'Destaques' },
            graffiti: { details: 'A noite', photos: 'A galeria' },
            'retro-pop': { details: 'O roteiro', photos: 'Flashback' },
            quince: { ourStory: ['Sobre {name}', 'Sobre a debutante'], details: 'A comemoração', photos: ['Momentos de {name}', 'Momentos inesquecíveis'],
                rsvpBlurb: ['Avise {name} se você vai poder comemorar com ela.', 'Avise a gente se você vai poder comemorar com a gente.'] }
        }
    };

    // The label slots a party template fills, as [labels group | null, key].
    const PARTY_LABEL_SLOTS = [
        ['sections', 'ourStory'], ['sections', 'details'], ['sections', 'photos'], ['sections', 'travel'],
        [null, 'rsvpBlurb'], [null, 'registryBlurb']
    ];

    function partyLabel(lang, templateId, key, name) {
        const L = PARTY_SITE_DEFAULTS[lang] || PARTY_SITE_DEFAULTS.en;
        const own = (QUINCE_TEMPLATE_IDS.includes(templateId) ? L.quince : L[templateId]) || {};
        let v = own[key] !== undefined ? own[key] : L[key];
        if (Array.isArray(v)) v = name ? v[0].replace('{name}', name) : v[1];
        return typeof v === 'string' ? v : '';
    }

    function _eventTimeFrom(text) {
        const m = String(text || '').match(/(\d{1,2})[:.h](\d{2})\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/i);
        if (!m) return null;
        let h = parseInt(m[1], 10);
        const min = m[2];
        const ap = (m[3] || '').toLowerCase().replace(/[^ap]/g, '');
        if (ap === 'p' && h < 12) h += 12;
        if (ap === 'a' && h === 12) h = 0;
        if (h > 23 || parseInt(min, 10) > 59) return null;
        return `${String(h).padStart(2, '0')}:${min}`;
    }

    // "Rena & Akiva's Wedding", "Mariage Oscar & Ange", "Sarah and Michael" →
    // two names. Anything else (one honoree, a company) returns null and the host
    // types them: a wrong guess on the hero is worse than an empty field.
    function _coupleNamesFrom(title) {
        let s = String(title || '')
            .replace(/[’']s\b/gi, '')
            .replace(/\b(the|our|wedding|weddings|mariage|boda|hochzeit|casamento|matrimonio|bruiloft|engagement|celebration|party|of|de|da|do|von|le|la|el|nuestra)\b/gi, ' ')
            // \b is ASCII-only, so the Hebrew/Arabic wedding words go by spaces.
            .replace(/(^|\s)(החתונה|חתונת|חתונה|של|حفل|زفاف|عرس)(?=\s|$)/g, ' ')
            .replace(/\b(19|20)\d{2}\b/g, ' ')
            .replace(/[!.,:;|()"“”–—]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const m = s.match(/^(\p{L}[\p{L}’'-]*)\s*(?:&|\+|❤️|❤|♥|💍|🤍|💕|\band\b|\by\b|\bet\b|\bund\b|\be\b|\bve\b)\s*(\p{L}[\p{L}’'-]*)$/iu)
            // "רנה ועקיבא", "سارة وأحمد": the "and" is a prefix on the second name.
            || s.match(/^(\p{L}+)\s+[וو](\p{L}{2,})$/u);
        if (!m) return null;
        const tidy = (n) => (n === n.toUpperCase() ? n.charAt(0) + n.slice(1).toLowerCase() : n);
        return [tidy(m[1]), tidy(m[2])];
    }

    // Names the AI read from the event's words (occasionAI.names) are used only
    // when it was sure; every name it returns appears in the host's own text.
    const AI_NAMES_MIN_CONFIDENCE = 0.7;

    // Who heads the site: two names for a wedding template, the guest of honor
    // for a party one. The AI first, the title pattern when it had nothing sure.
    function siteNamesFor(form, ai, template) {
        const aiNames = ai && ai.namesConfidence >= AI_NAMES_MIN_CONFIDENCE && Array.isArray(ai.names) ? ai.names : [];
        if (isPartyTemplate(template)) return aiNames.length ? [aiNames.join(' & ')] : null;
        if (aiNames.length === 2) return aiNames;
        return _coupleNamesFrom(form && form.title);
    }

    // ---- Which design an event is shown in (3 critics, 2026-11-03) ---------
    // docs/site-preview-campaign-plan.md has the table and the reasons. Modern
    // Minimalist is never offered: without photos it reads as an unstyled page.
    // A backdrop is chosen per design (the first one is not always the best:
    // Graffiti's concrete, Retro Pop's vinyl, Quince Noche's night garden).
    const D = (template, backdropId) => ({ template, backdropId });
    const LOOK = {
        blush: D('romantic-blush', 'blush-pressed-florals'),
        garden: D('garden-party', 'garden-pressed-greenery'),
        elegance: D('classic-elegance', 'elegance-gold-leaf'),
        coastal: D('coastal-breeze', null),
        sunset: D('sunset-romance', null),
        khaleeji: D('khaleeji', null),
        rosa: D('quince-rosa', null),
        noche: D('quince-noche', 'noche-gold-constellation'),
        fiestaConfetti: D('fiesta', 'fiesta-confetti-burst'),
        fiestaPapel: D('fiesta', 'fiesta-papel-picado'),
        fiestaMarigold: D('fiesta', 'fiesta-marigold-scatter'),
        graffiti: D('graffiti', 'graffiti-neon-drip'),
        retro: D('retro-pop', 'retro-sunburst-paper')
    };

    // Age from the title: "1st", "Primer", "aninho", "Sweet 16", "30th",
    // "יום הולדת 30", "عيد ميلاد ٣٠", "60. Geburtstag". null when none.
    function ageFromTitle(title) {
        const t = String(title || '').replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))).toLowerCase();
        if (/\b(first|primer|primeiro|premier|erste[rn]?)\b|\baninho\b|\bkindergeburtstag\b/.test(t)) return 1;
        if (/sweet\s*1[56]/.test(t)) return 16;
        const m = t.match(/\b(\d{1,3})\s*(st|nd|rd|th|º|°|\.|er|e|ème|años|anos|ans|jahre)?\b/);
        if (!m) return null;
        const n = parseInt(m[1], 10);
        return n >= 1 && n <= 110 ? n : null;
    }

    // { template, backdropId, strip: [{ template, backdropId }] } for a form.
    function designFor(form, { lang, country } = {}) {
        const f = form || {};
        const L = String(lang || f.defaultLanguage || f.lang || 'en').slice(0, 2).toLowerCase();
        const occ = EC && EC.occasionOf ? EC.occasionOf(f) : null;
        const cat = EC ? EC.categorizeForm(f) : 'Other';
        const v = occ ? occ.value : null;
        const fiesta = L === 'es' ? LOOK.fiestaPapel : LOOK.fiestaConfetti;
        const pick = (main, ...strip) => ({ ...main, strip: [main, ...strip] });
        if (v === 'henna' || v === 'katb_kitab') {
            return String(country || '').toUpperCase() === 'IN'
                ? pick(LOOK.elegance, LOOK.fiestaMarigold, LOOK.blush)
                : pick(LOOK.khaleeji, LOOK.elegance, LOOK.blush);
        }
        if (cat === 'Wedding & Engagement' || cat === 'Anniversary') {
            if (L === 'ar') return pick(LOOK.khaleeji, LOOK.elegance, LOOK.blush);
            if (String(country || '').toUpperCase() === 'IN') return pick(LOOK.elegance, LOOK.khaleeji, LOOK.blush);
            if (L === 'he' || L === 'fr' || L === 'de') return pick(LOOK.garden, LOOK.blush, LOOK.elegance, LOOK.sunset);
            if (L === 'pt') return pick(LOOK.sunset, LOOK.garden, LOOK.blush, LOOK.coastal);
            return pick(LOOK.blush, LOOK.garden, LOOK.elegance, LOOK.coastal);
        }
        if (cat === 'Quinceañera & Sweet 16') {
            return ageFromTitle(f.title) === 16 ? pick(LOOK.graffiti, LOOK.fiestaConfetti, LOOK.retro) : pick(LOOK.rosa, LOOK.noche, LOOK.elegance);
        }
        if (cat === 'Bar/Bat Mitzvah') return pick(LOOK.elegance, LOOK.graffiti, LOOK.blush);
        if (cat === 'Baby & Gender Reveal') return pick(LOOK.garden, LOOK.blush, LOOK.fiestaConfetti);
        if (cat === 'Graduation') return pick(LOOK.elegance, LOOK.fiestaConfetti, LOOK.retro);
        if (cat === 'Birthday' || cat === 'Party') {
            const age = ageFromTitle(f.title);
            if (age != null && age <= 12) return pick(fiesta, LOOK.garden, LOOK.blush);
            if (age != null && age <= 19) return pick(LOOK.graffiti, fiesta, LOOK.retro);
            if (age != null && age >= 50) return pick(LOOK.elegance, LOOK.retro, LOOK.garden);
            return pick(fiesta, LOOK.retro, LOOK.graffiti);
        }
        return null; // no design fits (corporate, holiday, unknown)
    }

    // The picture the host put on their RSVP form, already public on the RSVP
    // page: their upload, or the gallery picture they chose for it (Donny,
    // 2026-11-03: they picked it, and it beats a blank hero). Never the
    // builder's default image, which nobody chose. Callers drop a tall upload
    // (usually a photo of a printed invitation) for the design's art.
    function photoFor(form) {
        const f = form || {};
        const cands = [f.uploadedImageUrl, f.imageSrc].map((u) => String(u || '').trim()).filter(Boolean);
        for (const u of cands) {
            if (/^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\//.test(u)) return u;
            const g = u.match(/^(?:https:\/\/quikrsvp\.com)?(\/assets\/images\/gallery-[a-z0-9-]+\.(?:webp|jpg|jpeg|png))$/i);
            if (g) return g[1];
        }
        return null;
    }

    // The music the host put on their RSVP form, and only that (Donny,
    // 2026-11-03): a song we chose could be wrong for the couple's culture or
    // taste; theirs never is. It starts when the guest taps the envelope or the
    // cover, so the browser allows it. null = a quiet site (91% of forms).
    function musicFor(form) {
        const m = form && form.backgroundMusic;
        if (!m || m.enabled !== true) return null;
        const url = String(m.url || '');
        const ok = /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\//.test(url)
            || /^\/assets\/audio\/music\/[a-z0-9-]+\.mp3$/.test(url);
        if (!ok) return null;
        return { enabled: true, url, name: String(m.name || '').slice(0, 80), source: m.source === 'library' ? 'library' : 'upload',
            trackId: typeof m.trackId === 'string' ? m.trackId.slice(0, 60) : null, rightsConfirmedAt: m.rightsConfirmedAt || null };
    }

    // The public RSVP link the site's button opens (same shape the builder stores).
    function formUrlFor(form, formId) {
        const slug = form && (form.slug || form.customUrl);
        return slug && /^[a-z0-9-]+$/i.test(slug) ? 'https://quikrsvp.com/' + slug : 'https://quikrsvp.com/form?formId=' + formId;
    }

    function dateSeconds(eventDate) {
        if (!eventDate) return null;
        if (eventDate._seconds || eventDate.seconds) return eventDate._seconds || eventDate.seconds;
        const d = new Date(String(eventDate).slice(0, 10) + 'T12:00:00Z');
        return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
    }

    // The schedule: one event per part of the form, else one from its location.
    // Same rule as the builder's applyEventDetails. `esc` turns text into HTML.
    function eventsFor(form, esc) {
        const blank = { name: '', date: '', time: '', venue: '', address: '', dressCode: '', description: '',
            notes: '', imageUrl: '', showGoogleMaps: true, showWaze: true, tags: [] };
        const parts = (Array.isArray(form.eventParts) ? form.eventParts : []).filter((p) => p && (p.name || p.where || p.when));
        if (parts.length) {
            return parts.map((p) => {
                const when = String(p.when || '').trim();
                const at = _eventTimeFrom(when);
                // Keep the host's own wording whenever it says more than a time.
                const extra = when.replace(/(\d{1,2})[:.h](\d{2})\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/i, '').replace(/[\s,.\-–h]+/g, '');
                return {
                    ...blank,
                    name: String(p.name || '').trim(),
                    time: at || '',
                    venue: String(p.where || '').trim(),
                    description: when && (!at || extra) ? `<p>${esc(when)}</p>` : '',
                    tags: (Array.isArray(p.tags) ? p.tags : []).filter((tag) => typeof tag === 'string' && tag.trim() && !/^https?:|\//i.test(tag))
                };
            });
        }
        if (form.includeLocation !== false && String(form.eventLocation || '').trim()) {
            const venue = String(form.eventLocation).trim();
            const addr = String(form.locationAddress || '').trim();
            return [{ ...blank, name: String(form.title || '').trim(), time: _eventTimeFrom(form.eventTime) || '', venue,
                address: addr && addr !== venue && !/https?:/i.test(addr) ? addr : '' }];
        }
        return [];
    }

    const escHtml = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    /**
     * The whole site, as site.html's builder-preview mode reads it
     * (wedding-builder.js _buildPreviewData has the same shape). Only what the
     * form knows is filled: names, date, time, schedule, RSVP. Travel, FAQ,
     * photos and registry stay off — they would be empty.
     */
    function buildSite(form, formId, ai, { template, backdropId, lang } = {}) {
        const f = form || {};
        const L = String(lang || f.defaultLanguage || f.lang || 'en').slice(0, 2).toLowerCase();
        const names = siteNamesFor(f, ai, template) || [];
        const party = isPartyTemplate(template);
        const first = String(names[0] || '').trim().split(/\s+/)[0] || '';
        const labels = { nav: {}, sections: {}, countdown: {}, travel: {}, registryBlurb: '', rsvpBlurb: '' };
        if (party) {
            PARTY_LABEL_SLOTS.forEach(([group, key]) => {
                const holder = group ? labels[group] : labels;
                holder[key] = partyLabel(PARTY_SITE_DEFAULTS[L] ? L : 'en', template, key, first);
            });
        }
        const welcome = party ? (PARTY_SITE_DEFAULTS[L] || PARTY_SITE_DEFAULTS.en).welcome
            : (LOCALIZED_WELCOME_MESSAGES[L] || LOCALIZED_WELCOME_MESSAGES.en);
        const sec = dateSeconds(f.eventDate);
        return {
            template,
            partner1Name: names[0] || '',
            partner2Name: names[1] || '',
            weddingDate: sec ? { _seconds: sec } : null,
            dateDisplayText: '',
            language: L,
            pages: {
                home: { enabled: true, welcomeMessage: welcome, heroImageUrl: photoFor(f), backdropId: backdropId || null, customBackdrop: null,
                    heroLayout: null, mobileHeroLayout: null, ourStory: '', invitation: null, documents: [] },
                details: { enabled: true, events: eventsFor(f, escHtml), documents: [] },
                travel: { enabled: false, hotels: [], documents: [], airports: '', directions: '', localTips: '' },
                faq: { enabled: false, questions: [] },
                photos: { enabled: false, galleryImages: [] },
                registry: { enabled: false, registryId: null, items: [] },
                rsvp: { enabled: true, formUrl: formUrlFor(f, formId), openInNewTab: false }
            },
            pageOrder: [],
            // Opens straight on the site, no envelope or cover first: the email
            // promises "already filled in" and a host on a phone should see
            // theirs at once (Fable, 2026-11-03). The host's own music, if any,
            // plays from the site's music button.
            settings: { password: null, showCountdown: true, showHijriDate: false, envelopeEnabled: false, envelopeStyle: null,
                envelopeColor: null, openingStyle: null, coverImage: null,
                backgroundMusic: musicFor(f),
                startTime: _eventTimeFrom(f.eventTime), navigationStyle: 'scroll' },
            theme: { primaryColor: null, accentColor: null, fontHeading: null, fontBody: null },
            labels,
            heroVideo: null,
            previewFocus: 'home'
        };
    }

    const SiteFromForm = {
        PARTY_TEMPLATE_IDS, QUINCE_TEMPLATE_IDS, ENVELOPE_TEMPLATES, isPartyTemplate, photoFor, musicFor,
        LOCALIZED_WELCOME_MESSAGES, PARTY_SITE_DEFAULTS, PARTY_LABEL_SLOTS, partyLabel,
        AI_NAMES_MIN_CONFIDENCE, eventTimeFrom: _eventTimeFrom, coupleNamesFrom: _coupleNamesFrom, siteNamesFor,
        ageFromTitle, designFor, formUrlFor, eventsFor, buildSite
    };
    if (typeof window !== 'undefined') window.SiteFromForm = SiteFromForm;
    if (typeof module !== 'undefined' && module.exports) module.exports = SiteFromForm;
})();
